import type React from 'react'
import {createContext, use, useMemo, useState} from 'react'

/**
 * Context for search term state.
 * Split from dispatch context to prevent re-renders when only reading state.
 */
const SearchTermStateContext = createContext<string>('')

/**
 * Context for search term dispatch function.
 * Split from state context to prevent re-renders when only setting state.
 */
const SearchTermDispatchContext = createContext<(term: string) => void>(() => undefined)

interface SearchTermProviderProps {
  children: React.ReactNode
}

/**
 * Provider for search term state and dispatch.
 * Uses split contexts to optimize re-renders:
 * - Components that only read searchTerm won't re-render when setSearchTerm changes
 * - Components that only call setSearchTerm won't re-render when searchTerm changes
 */
export function SearchTermProvider({children}: SearchTermProviderProps) {
  const [searchTerm, setSearchTerm] = useState('')

  return (
    <SearchTermStateContext value={searchTerm}>
      <SearchTermDispatchContext value={setSearchTerm}>{children}</SearchTermDispatchContext>
    </SearchTermStateContext>
  )
}

/**
 * Hook to read the current search term.
 * Only re-renders when searchTerm changes.
 */
export function useSearchTerm(): string {
  return use(SearchTermStateContext)
}

/**
 * Hook to get the setSearchTerm function.
 * Never re-renders due to search term changes.
 */
export function useSetSearchTerm(): (term: string) => void {
  return use(SearchTermDispatchContext)
}

/**
 * Hook to get both searchTerm and setSearchTerm.
 * Re-renders when searchTerm changes.
 * Use useSetSearchTerm() if you only need to set the term.
 */
export function useSearchTermContext(): {searchTerm: string; setSearchTerm: (term: string) => void} {
  const searchTerm = use(SearchTermStateContext)
  const setSearchTerm = use(SearchTermDispatchContext)

  return useMemo(() => ({searchTerm, setSearchTerm}), [searchTerm, setSearchTerm])
}
